import { sharedLib } from "@shared/services/shared.service";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { ClientData, QueryStatus } from "@shared/types";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";

interface useTradingTerminalQueriesProps {
  account?: ClientData;
  exchanges?: string[];
  subGroupIds?: number[];
  symbol: string;
  reverse?: boolean;
  isSignal?: boolean;
  isAdminWorkspace?: boolean;
}

const useTradingTerminalQueries = ({
  account,
  exchanges,
  subGroupIds,
  symbol,
  reverse,
  isSignal,
  isAdminWorkspace,
}: useTradingTerminalQueriesProps) => {
  const { isSymbolActiveInAccount } = useSymbolsMetadata();

  const options = {
    refetchOnMountOrArgChange: true,
    skip: !isSymbolActiveInAccount(symbol, account?.client_id),
  };
  const baseArgs = {
    symbol: symbol || "",
    account_id: account?.client_id || 0,
  };

  const getSymbolsMetadataStatus =
    sharedLib.sharedApi.common.useGetSymbolsMetadataQuery();

  const getClientsListQuery =
    sharedLib.sharedApi.configuration.useGetClientsListQuery();

  const getAccountsMainCoinsAvailableAmountQuery =
    sharedLib.sharedApi.bots.useGetAccountsMainCoinsAvailableAmountQuery(
      isSignal
        ? skipToken
        : {
            client_id: account?.client_id,
          }
    );

  const getCreateTradeGeneralDataQuery =
    sharedLib.sharedApi.trades.useGetCreateTradeGeneralDataQuery(
      isSignal ? skipToken : { ...baseArgs, include_without: false },
      options
    );

  const getSignalCreateGeneralDataArgs = useMemo(() => {
    if (!symbol || !exchanges?.length || !subGroupIds?.length) return skipToken;
    return {
      exchanges,
      symbol,
      sub_group_ids: subGroupIds,
    };
  }, [symbol, exchanges, subGroupIds, isAdminWorkspace]);

  const getSignalCreateGeneralDataQuery =
    sharedLib.sharedApi.signals.useGetSignalCreateGeneralDataQuery(
      getSignalCreateGeneralDataArgs,
      { refetchOnMountOrArgChange: true }
    );
  const getGroupsDataQuery = sharedLib.sharedApi.settings.useGetGroupsDataQuery(
    isSignal && !isAdminWorkspace ? undefined : skipToken
  );
  const getAdminSubGroupsStatus =
    sharedLib.sharedApi.admin.useGetAdminSubGroupsQuery(
      isSignal && isAdminWorkspace ? undefined : skipToken
    );

  const getAvailableQuantityQuery =
    sharedLib.sharedApi.common.useGetAvailableQuantityQuery(
      isSignal ? skipToken : { ...baseArgs, reverse: !!reverse },
      options
    );

  const [freeTextCreateTrade, { isLoading: freeTextCreateTradeLoading }] =
    sharedLib.sharedApi.trades.useFreeTextCreateTradeMutation();

  const [createTrade, { isLoading: createTradeLoading }] =
    sharedLib.sharedApi.trades.useCreateTradeMutation();
  const [createSignal, { isLoading: createSignalLoading }] =
    sharedLib.sharedApi.signals.useCreateSignalMutation();

  const queryStatuses: QueryStatus[] = [
    getClientsListQuery,
    getCreateTradeGeneralDataQuery,
    getSymbolsMetadataStatus,
  ];
  if (!isSignal) {
    queryStatuses.push(getAvailableQuantityQuery);
    queryStatuses.push(getAccountsMainCoinsAvailableAmountQuery);
  } else {
    queryStatuses.push(getSignalCreateGeneralDataQuery);
    if (isAdminWorkspace) {
      queryStatuses.push(getAdminSubGroupsStatus);
    } else {
      queryStatuses.push(getGroupsDataQuery);
    }
  }

  const isLoading = queryStatuses.some((queryStatus) => {
    return (
      queryStatus.isLoading ||
      (queryStatus.endpointName &&
        ["getCreateTradeGeneralData", "getAvailableQuantity"].includes(
          queryStatus.endpointName
        ) &&
        queryStatus.isUninitialized) ||
      !symbol ||
      (!isSignal && !account) ||
      (isSignal && !subGroupIds)
    );
  });

  const isFetching =
    getCreateTradeGeneralDataQuery.isFetching ||
    getSignalCreateGeneralDataQuery.isFetching ||
    getAvailableQuantityQuery.isFetching;

  const isError = queryStatuses.some((queryStatus) => queryStatus.isError);

  const refetchErrorQueries = () => {
    queryStatuses.forEach((queryStatus) => {
      if (queryStatus.isError && !queryStatus.data) {
        try {
          queryStatus.refetch();
        } catch {}
      }
    });
  };

  return {
    getCreateTradeGeneralDataQuery,
    getSignalCreateGeneralDataQuery,
    getAvailableQuantityQuery,
    getGroupsDataQuery,
    getAdminSubGroupsStatus,
    createTrade,
    createTradeLoading,
    createSignal,
    createSignalLoading,
    isLoading,
    isFetching,
    freeTextCreateTrade,
    freeTextCreateTradeLoading,
    isError,
    refetchErrorQueries,
  };
};

export default useTradingTerminalQueries;
